  <section class="dashboard-draft">
    <!-- Full-screen loading veil for Publish: stays up through the save call,
         the layout-API publish call, AND the router navigation to the
         published screen, so there's no flash of an idle button while the
         destination route is still loading in. Reparented to <body> in
         ngAfterViewInit (kept in the DOM always, toggled via `.active`) so
         `position: fixed` centers on the real viewport regardless of scroll
         position or any transformed ancestor. -->
    <div class="dashboard-publish-overlay" [class.active]="dashboardActionPending === 'publish'" #publishOverlay>
      <div class="dashboard-publish-overlay-spinner"></div>
      <p>Publishing dashboard…</p>
    </div>
    <ng-template #globalFiltersBar let-orientation="orientation">
      <div
        class="global-filters-bar ng-select-bar"
        *ngIf="dashboardGlobalFilters.length && dashboardCards.length"
        [class.global-filters-vertical]="orientation === 'vertical'"
      >
        <h3 class="global-filters-title">Global filters</h3>
        <div
          class="global-filter"
          *ngFor="let filter of sortedDashboardGlobalFilters"
          [class.filter-cascaded]="filter.parentKey"
          [class.filter-disabled]="!isFilterEnabled(filter)"
          [style.margin-left.px]="orientation === 'vertical' ? 0 : 16 * getFilterDepth(filter)"
        >
          <label>
            <span class="cascade-prefix" *ngIf="filter.parentKey">└─</span>
            {{ filter.label }}
            <span class="filter-type-badge" [class.badge-cascading]="filter.parentKey">
              {{ filter.parentKey ? 'Cascading' : (filter.isMultiSelect ? 'Multi' : 'Single') }}
            </span>
          </label>

          <ng-select
            *ngIf="filter.isMultiSelect"
            #gfMultiSel
            class="global-filter-ngselect ss-select"
            [class.is-open]="!!gfMultiSel.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="getGlobalFilterOptions(filter)"
            [multiple]="true"
            [closeOnSelect]="false"
            [searchable]="true"
            [clearable]="true"
            [hideSelected]="false"
            [disabled]="!isFilterEnabled(filter)"
            [placeholder]="getFilterPlaceholder(filter)"
            [ngModel]="getActiveFilterValue(filter)"
            (ngModelChange)="onGlobalFilterChange(filter, $event)"
          >
            <ng-template ng-header-tmp>
              <div class="ng-select-header">
                <label class="select-all-checkbox">
                  <input
                    type="checkbox"
                    [checked]="isAllSelectedForFilter(filter)"
                    (change)="toggleSelectAll(filter)"
                  />
                  Select all
                </label>
              </div>
            </ng-template>
            <ng-template ng-option-tmp let-item="item" let-item$="item$">
              <span class="ng-option-row">
                <input type="checkbox" [checked]="item$.selected" class="ng-option-check" />
                {{ item }}
              </span>
            </ng-template>
          </ng-select>

          <ng-select
            *ngIf="!filter.isMultiSelect"
            #gfSingleSel
            class="global-filter-ngselect ss-select"
            [class.is-open]="!!gfSingleSel.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="getGlobalFilterOptions(filter)"
            [multiple]="false"
            [searchable]="true"
            [clearable]="true"
            [disabled]="!isFilterEnabled(filter)"
            [placeholder]="getFilterPlaceholder(filter)"
            [ngModel]="getActiveFilterValue(filter)[0] || null"
            (ngModelChange)="onGlobalFilterChange(filter, $event)"
          ></ng-select>
        </div>
        <button
          type="button"
          class="secondary-action clear-global-filters-btn"
          [disabled]="!hasActiveGlobalFilters"
          (click)="clearAllGlobalFilters()"
        >
          Clear all
        </button>
      </div>
    </ng-template>

    <div class="saved-dashboards-panel dashboard-saved-top" *ngIf="showSavedDashboardPicker">
      <h3>Saved dashboards</h3>
      <div *ngIf="!savedDashboards.length" class="saved-empty">
        No saved dashboards yet.
      </div>
      <button
        type="button"
        class="saved-dashboard"
        *ngFor="let dashboard of savedDashboards"
        [class.active]="dashboard.id === activeSavedDashboardId"
        (click)="loadSavedDashboard(dashboard)"
      >
        <span>
          <strong>{{ dashboard.name }}</strong>
          <small>
            <ng-container *ngIf="!isApiOnlyDashboard(dashboard)">{{ dashboard.items.length }} charts</ng-container>
            <span
              class="status-badge status-badge-inline"
              [class.status-published]="isPublished(dashboard)"
              [class.status-draft]="!isPublished(dashboard)"
            >
              {{ isPublished(dashboard) ? 'Published' : 'Draft' }}
            </span>
          </small>
        </span>
        <span
          *ngIf="!isApiOnlyDashboard(dashboard)"
          class="delete-chart"
          role="button"
          tabindex="0"
          aria-label="Delete saved dashboard"
          (click)="deleteSavedDashboard(dashboard, $event)"
        >
          Delete
        </span>
      </button>
    </div>

    <div class="dashboard-draft-header">
      <div>
        <h2>
          {{ dashboardName || 'Untitled dashboard' }}
          <span
            class="status-badge"
            [class.status-published]="dashboardStatus === 'published'"
            [class.status-draft]="dashboardStatus === 'draft'"
          >
            {{ dashboardStatus === 'published' ? 'Published' : 'Draft' }}
          </span>
        </h2>
        <p *ngIf="dashboardStatus === 'published' && dashboardPublishedAt">
          Published {{ dashboardPublishedAt | date: 'medium' }}
        </p>
        <p *ngIf="dashboardStatus !== 'published'"></p>
      </div>
      <div class="dashboard-actions">
        <button
          type="button"
          class="secondary-action"
          (click)="startNewDashboard()"
        >
          New Dashboard
        </button>
        <button
          type="button"
          class="secondary-action"
          [disabled]="!dashboardItems.length"
          (click)="clearDashboardDraft()"
        >
          Clear Dashboard
        </button>
      </div>
    </div>


    <div class="dashboard-live-context">
      <div>
        <span class="context-label">Current dashboard</span>
        <strong>{{ dashboardName || 'Untitled dashboard' }}</strong>
      </div>
      <div>
        <span class="context-label">Components</span>
        <strong>{{ dashboardItems.length }}</strong>
      </div>
      <div>
        <span class="context-label">Filters</span>
        <strong>{{ dashboardGlobalFilters.length }}</strong>
      </div>
      <div>
        <span class="context-label">Grid</span>
        <strong>{{ dashboardColumns }} columns</strong>
      </div>
    </div>

    <!-- Reusable tab switcher for tabbed dashboards. Shown at the top of the
         Components and Arrange steps so the user picks which tab they're editing;
         newly added charts land in the active tab. -->
    <ng-template #tabSwitcher>
      @if (useTabs) {
      <div class="ss-tab-switcher" role="tablist" aria-label="Dashboard tabs">
        @for (tab of dashboardTabs; track tab.id) {
        <div class="ss-tab-chip" [class.active]="tab.id === activeBuilderTabId">
          <button type="button" class="ss-tab-chip-main" role="tab"
                  [attr.aria-selected]="tab.id === activeBuilderTabId"
                  (click)="switchBuilderTab(tab.id)">
            {{ tab.label }}
          </button>
          <button type="button" class="ss-tab-chip-icon" title="Rename tab"
                  (click)="renameTabPrompt(tab)">✎</button>
          <button type="button" class="ss-tab-chip-icon" title="Move left"
                  [disabled]="$first" (click)="moveTab(tab.id, 'left')">‹</button>
          <button type="button" class="ss-tab-chip-icon" title="Move right"
                  [disabled]="$last" (click)="moveTab(tab.id, 'right')">›</button>
          <button type="button" class="ss-tab-chip-icon danger" title="Delete tab"
                  [disabled]="dashboardTabs.length <= 1" (click)="deleteTab(tab.id)">✕</button>
        </div>
        }
        <button type="button" class="ss-tab-add" (click)="addTab()">+ Tab</button>
      </div>
      }
    </ng-template>

    <!-- ████ Stepper driven by the JSON layout config (see JSON_CONFIG_PLAN.md);
         each step body is a named <ng-template> resolved by its field id. The
         default layout reproduces the original 4-step order exactly, so the
         index-based onDashboardStepChange logic is unaffected. ████ -->
    <mat-stepper
      class="dashboard-stepper"
      [orientation]="layout.stepperOrientation || 'horizontal'"
      [linear]="!!layout.linear"
      #dashboardStepper
      (selectionChange)="onDashboardStepChange($event.selectedIndex)"
    >
    <!-- ============== Setup ============== -->
    @if (isStepVisible('setup')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('setup') }}</ng-template>
        <div class="step-content">
          <div class="dashboard-controls">
            <div class="control-group">
              <label for="dashboardName">Dashboard name</label>
              <input
                #dashboardNameInput
                id="dashboardName"
                type="text"
                [(ngModel)]="dashboardName"
                (ngModelChange)="onDashboardNameChange()"
                placeholder="Name this dashboard"
              />
            </div>

            <div class="control-group">
              <label for="dashboardColumns">Grid columns</label>
              <ng-select
                labelForId="dashboardColumns"
                #ssSel
                class="ss-select"
                [class.is-open]="!!ssSel.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                [items]="dashboardColumnOptions"
                [clearable]="false"
                [searchable]="false"
                [ngModel]="dashboardColumns"
                (ngModelChange)="setDashboardColumns($event)"
              >
                <ng-template ng-label-tmp let-item="item">
                  {{ item }} column{{ item > 1 ? 's' : '' }}
                </ng-template>
                <ng-template ng-option-tmp let-item="item">
                  {{ item }} column{{ item > 1 ? 's' : '' }}
                </ng-template>
              </ng-select>
            </div>

            <div class="control-group ss-use-tabs-control">
              <label>Views</label>
              <label class="ss-toggle">
                <input
                  type="checkbox"
                  [checked]="useTabs"
                  (change)="toggleUseTabs($any($event.target).checked)"
                />
                <span>Use tabs (multiple views in one dashboard)</span>
              </label>
              <small class="ss-hint" *ngIf="useTabs">
                Add tabs like “Percentage” / “Dollar”, then assign charts to each tab.
              </small>
            </div>

          </div>
          <div class="step-nav">
            <span></span>
            <button mat-flat-button color="primary" matStepperNext>Next: Components →</button>
          </div>
        </div>
      </mat-step>
    }

    <!-- ============== Components ============== -->
    @if (isStepVisible('components')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('components') }}</ng-template>
        <div class="step-content">
          <ng-container *ngTemplateOutlet="tabSwitcher"></ng-container>
          <div class="components-repo step-embedded">
      <div class="components-repo-header">
        <h3>Component Repository</h3>
        <!-- In tabbed mode the count reflects the ACTIVE tab (the cards shown as
             added are this tab's only), so it matches what's on screen. The badge
             renders ONLY when there's a non-zero count — an empty tab shows no
             pill at all (not an empty green circle). -->
        <span class="component-selection-count" *ngIf="useTabs && visibleDashboardItems.length">
          {{ visibleDashboardItems.length }} in {{ activeBuilderTabLabel }}
        </span>
        <span class="component-selection-count" *ngIf="!useTabs && dashboardItems.length">
          {{ dashboardItems.length }} selected
        </span>
        <button class="go-to-chart-btn" (click)="startNewChartRequest.emit()" title="Go to Chart Builder">
          + New Chart
        </button>
      </div>
      <div *ngIf="!savedCharts.length" class="saved-empty">
        No saved components yet.
        <button class="go-to-chart-btn saved-empty-btn" (click)="startNewChartRequest.emit()">Go to Chart Builder</button>
      </div>
      <div class="components-grid gallery" *ngIf="savedCharts.length">
        <div
          class="component-tile gallery-card"
          *ngFor="let chart of savedCharts"
          [class.added]="getActiveTabChartCount(chart.id) > 0"
          [attr.data-kind]="getChartIconKind(chart)"
        >
          <!-- The card's "added" state is scoped to the tab being edited: on Tab 1
               only Tab 1's charts read as added; a chart that lives only in other
               tabs shows as fresh (un-added) here. -->
          <span class="added-count-badge" *ngIf="getActiveTabChartCount(chart.id) as addedCount">
            Added {{ addedCount }}
          </span>
          <button
            type="button"
            class="gallery-delete-btn"
            title="Delete this saved chart"
            (click)="deleteSavedChart(chart, $event)"
          >&#10005;</button>
          <div class="gallery-icon" [ngSwitch]="getChartIconKind(chart)" aria-hidden="true">
            <svg *ngSwitchCase="'axis-bar'" viewBox="0 0 64 48" fill="none" stroke="currentColor" stroke-width="0" stroke-linejoin="round">
              <rect x="6"  y="22" width="9" height="22" rx="1.5" fill="currentColor"/>
              <rect x="20" y="12" width="9" height="32" rx="1.5" fill="currentColor" opacity="0.85"/>
              <rect x="34" y="28" width="9" height="16" rx="1.5" fill="currentColor" opacity="0.65"/>
              <rect x="48" y="18" width="9" height="26" rx="1.5" fill="currentColor" opacity="0.45"/>
              <line x1="3" y1="44" x2="61" y2="44" stroke="currentColor" stroke-width="1.5" opacity="0.5"/>
            </svg>
            <svg *ngSwitchCase="'horizontal-bar'" viewBox="0 0 64 48" fill="none">
              <rect x="6" y="6"  width="40" height="7" rx="1.5" fill="currentColor"/>
              <rect x="6" y="16" width="52" height="7" rx="1.5" fill="currentColor" opacity="0.85"/>
              <rect x="6" y="26" width="28" height="7" rx="1.5" fill="currentColor" opacity="0.65"/>
              <rect x="6" y="36" width="46" height="7" rx="1.5" fill="currentColor" opacity="0.45"/>
            </svg>
            <svg *ngSwitchCase="'axis-line'" viewBox="0 0 64 48" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="4,38 16,24 28,30 40,14 52,20 60,10"/>
              <circle cx="16" cy="24" r="2.5" fill="currentColor"/>
              <circle cx="28" cy="30" r="2.5" fill="currentColor"/>
              <circle cx="40" cy="14" r="2.5" fill="currentColor"/>
              <circle cx="52" cy="20" r="2.5" fill="currentColor"/>
              <line x1="3" y1="44" x2="61" y2="44" stroke="currentColor" stroke-width="1.5" opacity="0.5"/>
            </svg>
            <svg *ngSwitchCase="'area'" viewBox="0 0 64 48" fill="none">
              <path d="M4 38 L16 22 L28 28 L40 12 L52 18 L60 8 L60 44 L4 44 Z" fill="currentColor" opacity="0.35"/>
              <polyline points="4,38 16,22 28,28 40,12 52,18 60,8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              <line x1="3" y1="44" x2="61" y2="44" stroke="currentColor" stroke-width="1.5" opacity="0.5"/>
            </svg>
            <svg *ngSwitchCase="'pie'" viewBox="0 0 64 48" fill="none">
              <circle cx="32" cy="24" r="18" fill="currentColor" opacity="0.35"/>
              <path d="M32 24 L32 6 A18 18 0 0 1 49.1 30.4 Z" fill="currentColor"/>
              <path d="M32 24 L49.1 30.4 A18 18 0 0 1 27 41.4 Z" fill="currentColor" opacity="0.65"/>
            </svg>
            <svg *ngSwitchCase="'donut'" viewBox="0 0 64 48" fill="none" stroke="currentColor" stroke-width="7">
              <circle cx="32" cy="24" r="14" opacity="0.3"/>
              <path d="M32 10 A14 14 0 0 1 45.6 28.8" stroke="currentColor"/>
              <path d="M45.6 28.8 A14 14 0 0 1 28 37.4" stroke="currentColor" opacity="0.7"/>
            </svg>
            <svg *ngSwitchCase="'scatter'" viewBox="0 0 64 48" fill="none">
              <line x1="3" y1="44" x2="61" y2="44" stroke="currentColor" stroke-width="1.5" opacity="0.5"/>
              <line x1="6" y1="4" x2="6" y2="44" stroke="currentColor" stroke-width="1.5" opacity="0.5"/>
              <circle cx="14" cy="34" r="3" fill="currentColor"/>
              <circle cx="22" cy="22" r="4" fill="currentColor" opacity="0.85"/>
              <circle cx="32" cy="28" r="3" fill="currentColor" opacity="0.75"/>
              <circle cx="40" cy="14" r="4" fill="currentColor"/>
              <circle cx="48" cy="22" r="3" fill="currentColor" opacity="0.65"/>
              <circle cx="55" cy="30" r="2.5" fill="currentColor" opacity="0.5"/>
            </svg>
            <svg *ngSwitchCase="'treemap'" viewBox="0 0 64 48" fill="none">
              <rect x="4"  y="4"  width="30" height="26" rx="1.5" fill="currentColor"/>
              <rect x="36" y="4"  width="24" height="14" rx="1.5" fill="currentColor" opacity="0.75"/>
              <rect x="36" y="20" width="24" height="10" rx="1.5" fill="currentColor" opacity="0.6"/>
              <rect x="4"  y="32" width="18" height="12" rx="1.5" fill="currentColor" opacity="0.85"/>
              <rect x="24" y="32" width="14" height="12" rx="1.5" fill="currentColor" opacity="0.5"/>
              <rect x="40" y="32" width="20" height="12" rx="1.5" fill="currentColor" opacity="0.4"/>
            </svg>
            <svg *ngSwitchCase="'kpi'" viewBox="0 0 64 48" fill="none">
              <rect x="6" y="6" width="52" height="36" rx="4" fill="currentColor" opacity="0.15"/>
              <text x="32" y="29" text-anchor="middle" font-family="ui-sans-serif, system-ui" font-size="18" font-weight="700" fill="currentColor">42K</text>
              <text x="32" y="40" text-anchor="middle" font-family="ui-sans-serif, system-ui" font-size="7" fill="currentColor" opacity="0.7">▲ 12%</text>
            </svg>
            <svg *ngSwitchCase="'table'" viewBox="0 0 64 48" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="5" y="6" width="54" height="36" rx="2" fill="currentColor" opacity="0.1"/>
              <line x1="5" y1="16" x2="59" y2="16" stroke="currentColor" stroke-width="2"/>
              <line x1="5" y1="26" x2="59" y2="26" opacity="0.5"/>
              <line x1="5" y1="34" x2="59" y2="34" opacity="0.5"/>
              <line x1="23" y1="6" x2="23" y2="42" opacity="0.5"/>
              <line x1="41" y1="6" x2="41" y2="42" opacity="0.5"/>
            </svg>
            <svg *ngSwitchDefault viewBox="0 0 64 48" fill="none">
              <rect x="6"  y="22" width="9" height="22" rx="1.5" fill="currentColor"/>
              <rect x="20" y="12" width="9" height="32" rx="1.5" fill="currentColor" opacity="0.85"/>
              <rect x="34" y="28" width="9" height="16" rx="1.5" fill="currentColor" opacity="0.65"/>
              <rect x="48" y="18" width="9" height="26" rx="1.5" fill="currentColor" opacity="0.45"/>
            </svg>
          </div>

          <div class="gallery-meta">
            <strong [title]="chart.name">{{ chart.name }}</strong>
            <small>{{ getChartTypeLabel(chart) }} · {{ chart.dataset.rows.length }} rows</small>
          </div>

          <!-- In tabbed mode, Add/Remove act on the ACTIVE tab, so the label and
               the Remove button key off the active-tab count — not the total —
               otherwise "+1 more" / "Remove" would misrepresent what happens. -->
          <div class="gallery-actions">
            <button
              type="button"
              class="secondary-action component-add-btn gallery-add-btn"
              [class.added]="getActiveTabChartCount(chart.id) > 0"
              [title]="useTabs ? ('Add to ' + activeBuilderTabLabel) : 'Add to dashboard'"
              (click)="addChartToDashboard(chart)"
            >
              {{ getActiveTabChartCount(chart.id) ? '+1 more' : '+ Add' }}
            </button>
            <button
              type="button"
              class="component-remove-one-btn"
              *ngIf="getActiveTabChartCount(chart.id) > 0"
              [title]="useTabs ? ('Remove one from ' + activeBuilderTabLabel) : 'Remove one'"
              (click)="removeLatestDashboardChart(chart.id)"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
          </div>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
            <button mat-flat-button color="primary" (click)="showDashboardPreviewStep()">Next: Preview →</button>
          </div>
        </div>
      </mat-step>
    }

    <!-- ============== Filters ============== -->
    @if (isStepVisible('filters')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('filters') }}</ng-template>
        <div class="step-content">
          <div class="global-filters-manage step-embedded">
      <h3>Global filters (dashboard-wide)</h3>
      <div class="formula-existing" *ngIf="dashboardGlobalFilters.length">
        <div class="formula-item filter-item" *ngFor="let filter of sortedDashboardGlobalFilters" [style.padding-left.px]="12 + 16 * getFilterDepth(filter)">
          <div class="formula-info">
            <strong>
              <span class="cascade-prefix" *ngIf="filter.parentKey">└─ </span>
              {{ filter.label }}
              <span class="filter-type-badge" [class.badge-cascading]="filter.parentKey">
                {{ filter.parentKey ? 'Cascading' : (filter.isMultiSelect ? 'Multi' : 'Single') }}
              </span>
            </strong>
            <small>
              column: {{ filter.key }} · {{ filter.isMultiSelect ? 'multi-select' : 'single-select' }}
              <span *ngIf="filter.parentKey"> · narrows by <em>{{ getParentFilterLabel(filter) }}</em></span>
            </small>
          </div>
          <button type="button" class="formula-remove" (click)="removeGlobalFilter(filter.key)">Remove</button>
        </div>
      </div>
      <div class="formula-add filter-add-grid">
        <input type="text" [(ngModel)]="newGlobalFilterLabel" placeholder="Label (e.g., Region)" />
        <input type="text" [(ngModel)]="newGlobalFilterKey" placeholder="Column name (e.g., Region)" />
        <ng-select
          *ngIf="dashboardGlobalFilters.length"
          #ssSel
          class="ss-select"
          [class.is-open]="!!ssSel.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
          title="Cascading parent (optional)"
          [items]="getCandidateParentFilters()"
          bindLabel="label"
          bindValue="key"
          [clearable]="true"
          [searchable]="true"
          placeholder="No parent"
          [ngModel]="newGlobalFilterParentKey || null"
          (ngModelChange)="newGlobalFilterParentKey = $event || ''"
          (clear)="newGlobalFilterParentKey = ''"
        >
          <ng-template ng-label-tmp let-item="item">Cascade from {{ item.label }}</ng-template>
          <ng-template ng-option-tmp let-item="item">Cascade from {{ item.label }}</ng-template>
        </ng-select>
        <label class="multi-toggle">
          <input type="checkbox" [(ngModel)]="newGlobalFilterMultiSelect" />
          Multi-select
        </label>
        <button type="button" class="secondary-action formula-add-btn" (click)="addGlobalFilter()">Add filter</button>
      </div>
      <!-- <small class="control-hint">
        Renders as a dashboard-wide dropdown above the chart grid. Filters every chart whose dataset has the column. Cascading filters narrow their options based on the parent's selection.
      </small> -->
      <small class="control-warning" *ngIf="newGlobalFilterError">{{ newGlobalFilterError }}</small>
    </div>

    <ng-container *ngTemplateOutlet="globalFiltersBar; context: { orientation: 'horizontal' }"></ng-container>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
            <button mat-flat-button color="primary" matStepperNext>Next: Arrange →</button>
          </div>
        </div>
      </mat-step>
    }

    <!-- ============== Arrange ============== -->
    @if (isStepVisible('arrange')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('arrange') }}</ng-template>
        <div class="step-content">
          <ng-container *ngTemplateOutlet="tabSwitcher"></ng-container>
          <div class="dashboard-preview-toolbar">
            <div>
              <span class="context-label">Dashboard preview</span>
              <strong>
                {{ dashboardItems.length }} chart{{ dashboardItems.length === 1 ? '' : 's' }} ·
                {{ dashboardGlobalFilters.length }} filter{{ dashboardGlobalFilters.length === 1 ? '' : 's' }}
              </strong>
            </div>
            <div class="dashboard-preview-actions">
              <button
                type="button"
                class="secondary-action dashboard-preview-action-btn"
                (click)="showDashboardComponentsStep()"
              >
                + Add charts
              </button>
              <button
                type="button"
                class="secondary-action dashboard-preview-action-btn"
                (click)="showDashboardFiltersStep()"
              >
                + Add filters
              </button>
            </div>
          </div>

          <div class="cross-filter-bar" *ngIf="hasActiveCrossFilters">
      <span class="cross-filter-bar-label">Cross-filters active</span>
      <button
        type="button"
        class="secondary-action clear-cross-filters-btn"
        (click)="clearAllCrossFilters()"
      >
        Clear all cross-filters
      </button>
    </div>

    <div
      class="global-filters-position-control"
      *ngIf="dashboardGlobalFilters.length && dashboardCards.length"
    >
      <span class="position-control-label">Global filters position:</span>
      <div class="position-control-options" role="group" aria-label="Global filters position">
        <button
          type="button"
          *ngFor="let opt of globalFiltersPositionOptions"
          class="position-option"
          [class.active]="dashboardGlobalFiltersPosition === opt.value"
          (click)="setGlobalFiltersPosition(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div *ngIf="!dashboardCards.length" class="empty-state dashboard-empty">
      Add a saved chart to start building a dashboard draft.
    </div>

    <div
      *ngIf="dashboardCards.length"
      class="arrange-with-filters"
      [attr.data-filter-pos]="dashboardGlobalFiltersPosition"
    >
      <ng-container
        *ngIf="dashboardGlobalFilters.length && (dashboardGlobalFiltersPosition === 'top' || dashboardGlobalFiltersPosition === 'left')"
      >
        <ng-container
          *ngTemplateOutlet="globalFiltersBar; context: { orientation: dashboardGlobalFiltersPosition === 'top' ? 'horizontal' : 'vertical' }"
        ></ng-container>
      </ng-container>

      <div
        class="dashboard-grid"
        [style.grid-template-columns]="'repeat(' + dashboardColumns + ', minmax(0, 1fr))'"
      >
      <article
        class="dashboard-card"
        *ngFor="let card of dashboardCards; let i = index; trackBy: trackDashboardCard"
        [style.grid-column]="'span ' + getEffectiveCardSpan(card)"
      >
        <header>
          <div class="pt-1">
            <h3>{{ card.chart.name }}</h3>
            <p>{{ card.chart.chartType }} · {{ card.chart.aggregation || 'raw' }}</p>
          </div>
          <div class="card-controls">
            <button
              type="button"
              class="card-control-btn"
              title="Move up"
              [disabled]="i === 0"
              (click)="moveDashboardItem(card.item, 'up')"
            >&#9650;</button>
            <button
              type="button"
              class="card-control-btn"
              title="Move down"
              [disabled]="i === dashboardCards.length - 1"
              (click)="moveDashboardItem(card.item, 'down')"
            >&#9660;</button>
            <ng-select
              #ssSel
              class="ss-select ss-select--inline card-control-span"
              [class.is-open]="!!ssSel.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
              title="Card width"
              [items]="spanOptionsForCurrentGrid"
              bindLabel="label"
              bindValue="value"
              [clearable]="false"
              [searchable]="false"
              [ngModel]="getEffectiveCardSpan(card)"
              (ngModelChange)="setCardSpan(card.item, $event)"
            ></ng-select>
            <button
              type="button"
              class="card-control-btn card-control-wire"
              title="Cross-filter wiring"
              [class.active]="expandedListenerEditor === card.item.id"
              (click)="toggleListenerEditor(card.item.id)"
            >&#9783;</button>
            <button
              type="button"
              class="card-control-btn card-control-edit"
              title="Edit chart (x-axis, formatting, filter keys)"
              (click)="editDashboardChart(card, $event)"
            >&#9998;</button>

            <button
              type="button"
              class="remove-dashboard-item"
              (click)="removeDashboardItem(card.item)"
            >
              Remove
            </button>
          </div>
        </header>

        <div class="listener-editor" *ngIf="expandedListenerEditor === card.item.id">
          <div class="listener-editor-row">
            <label>Publishes filter key</label>
            <ng-select
              #ssSel
              class="ss-select"
              [class.is-open]="!!ssSel.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
              [items]="card.chart.dataset.columns"
              bindLabel="name"
              bindValue="name"
              [clearable]="true"
              [searchable]="true"
              placeholder="(none — does not publish)"
              [ngModel]="getEffectiveFilterKey(card) || null"
              (ngModelChange)="setItemFilterKey(card.item, $event || '')"
              (clear)="setItemFilterKey(card.item, '')"
            ></ng-select>
          </div>
          <div class="listener-editor-row">
            <label>Components that react to this card</label>
            <div class="listener-checkboxes" *ngIf="dashboardCards.length > 1; else onlyOneCard">
              <label
                class="listener-checkbox"
                *ngFor="let target of dashboardCards"
                [class.disabled]="target.item.id === card.item.id"
              >
                <input
                  type="checkbox"
                  [disabled]="target.item.id === card.item.id"
                  [checked]="isItemListener(card.item, target.item.id)"
                  (change)="toggleItemListener(card.item, target.item.id)"
                />
                <span>{{ target.chart.name }} <small>({{ target.chart.chartType }})</small></span>
              </label>
            </div>
            <ng-template #onlyOneCard>
              <small class="control-hint">Add more components to wire listeners.</small>
            </ng-template>
          </div>
          <div class="listener-editor-actions">
            <button type="button" class="formula-remove" (click)="resetItemListeners(card.item)">Reset to chart defaults</button>
            <button type="button" class="secondary-action listener-done-btn" (click)="toggleListenerEditor(card.item.id)">Done</button>
          </div>
        </div>

        <div class="cross-filter-chips" *ngIf="getActiveFiltersForCard(card).length">
          <span
            class="cross-filter-chip"
            *ngFor="let cf of getActiveFiltersForCard(card)"
            [class.chip-self]="cf.isSelf"
          >
            <strong>{{ cf.sourceLabel }}:</strong>
            {{ cf.filterKey }} =
            <span
              class="chip-value"
              *ngFor="let v of cf.values; let last = last"
            >
              {{ v }}<a
                class="chip-remove"
                title="Remove this value"
                (click)="clearCrossFilterValue(cf.sourceChartId, v); $event.stopPropagation()"
              >×</a>{{ last ? '' : ', ' }}
            </span>
            <a
              class="chip-clear-all"
              title="Clear this filter"
              (click)="clearCrossFilterFromSource(cf.sourceChartId); $event.stopPropagation()"
            >clear</a>
          </span>
        </div>

        <ng-container *ngIf="isKpiCard(card); else nonKpi">
          <div class="dashboard-kpi" *ngIf="getKpiDisplay(card) as kpi">
            <div class="kpi-label">{{ kpi.label }}</div>
            <div class="kpi-value">{{ kpi.value }}</div>
            <div
              class="kpi-comparison"
              *ngIf="kpi.comparisonText"
              [class.kpi-up]="kpi.comparisonDirection === 'up'"
              [class.kpi-down]="kpi.comparisonDirection === 'down'"
              [class.kpi-flat]="kpi.comparisonDirection === 'flat'"
            >
              {{ kpi.comparisonText }}
            </div>
            <div class="kpi-meta">{{ kpi.aggregation }}</div>
          </div>
        </ng-container>
        <ng-template #nonKpi>
          <ng-container *ngIf="isDrillDownCard(card); else nonDrillDown">
            <lib-generic-drill-down
              class="dashboard-drilldown-live"
              [data]="getDrillDownCardData(card)"
              (filterSelect)="onDrillDownRowClick(card, $event)"
            ></lib-generic-drill-down>
          </ng-container>
          <ng-template #nonDrillDown>
          <ng-container *ngIf="isTableCard(card); else echartsBlock">
            <div class="dashboard-table" *ngIf="getTableDisplay(card) as tbl">
              <div class="dt-col-toolbar">
                <div class="dt-col-menu-wrapper">
                  <button type="button" class="dt-col-btn" (click)="toggleTableColumnMenu(card, $event)" title="Show / hide columns">
                    <span class="dt-col-icon">&#9776;</span> Columns
                  </button>
                  <div class="dt-col-menu" *ngIf="isTableColumnMenuOpen(card)" (click)="$event.stopPropagation()">
                    <div class="dt-col-menu-item" *ngFor="let column of tbl.allColumns"
                         (click)="toggleTableColumnVisibility(card, column.name, $event)">
                      <input type="checkbox" [checked]="isTableColumnVisible(card, column.name)"
                             (click)="toggleTableColumnVisibility(card, column.name, $event)" />
                      <span class="dt-col-menu-label">{{ column.name }}</span>
                    </div>
                  </div>
                </div>
                <button type="button" class="dt-col-btn dt-col-reset" (click)="resetTableColumns(card, $event)" title="Reset columns">
                  Reset
                </button>
              </div>
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th *ngFor="let column of tbl.columns">
                        <div class="dt-th-content">
                          <span class="dt-th-label" (click)="sortTableColumn(card, column.name, $event)">
                            {{ column.name }}
                            <span class="dt-sort-indicator" *ngIf="tableSortIndicator(card, column.name)">{{ tableSortIndicator(card, column.name) }}</span>
                          </span>
                          <span class="dt-th-controls">
                            <span class="dt-th-ctl" *ngIf="canMoveTableColumnLeft(card, column.name)"
                              (click)="moveTableColumnLeft(card, column.name, $event)" title="Move left">&#8592;</span>
                            <span class="dt-th-ctl" *ngIf="canMoveTableColumnRight(card, column.name)"
                              (click)="moveTableColumnRight(card, column.name, $event)" title="Move right">&#8594;</span>
                            <span class="dt-th-ctl dt-th-remove" (click)="removeTableColumn(card, column.name, $event)"
                              title="Remove column">&#10005;</span>
                          </span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      *ngFor="let row of tbl.rows"
                      class="table-row"
                      [class.table-row-clickable]="getEffectiveFilterKey(card)"
                      (click)="onTableRowClick(card, row)"
                    >
                      <td *ngFor="let column of tbl.columns">{{ row[column.name] }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="table-meta">
                {{ tbl.rows.length }} rows shown
                <span *ngIf="getEffectiveFilterKey(card)" class="table-hint"> · click a row to filter listeners by {{ getEffectiveFilterKey(card) }}</span>
              </div>
            </div>
          </ng-container>
          <ng-template #echartsBlock>
            <ng-container *ngIf="isWidgetCard(card); else realEchartsBlock">
              <app-self-service-widget
                class="dashboard-widget"
                [kind]="widgetKindForCard(card)"
                [config]="card.chart.widgetConfig"
                [title]="card.chart.name"
                [editable]="true"
                (configChange)="onWidgetConfigChange(card, $event)"
                (valueChange)="onWidgetValueChange(card, $event)"
              ></app-self-service-widget>
            </ng-container>
            <ng-template #realEchartsBlock>
              <div #dashboardChart class="dashboard-chart"></div>
            </ng-template>
          </ng-template>
          </ng-template>
        </ng-template>
      </article>
    </div>

      <ng-container
        *ngIf="dashboardGlobalFilters.length && dashboardGlobalFiltersPosition === 'right'"
      >
        <ng-container
          *ngTemplateOutlet="globalFiltersBar; context: { orientation: 'vertical' }"
        ></ng-container>
      </ng-container>
    </div>
          <div class="step-nav arrange-step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
            <div class="arrange-step-actions">
              <button
                type="button"
                class="primary-action arrange-save-action"
                [disabled]="!canSaveDashboard || !!dashboardActionPending"
                (click)="saveDashboard()"
              >
                <span class="btn-spinner" *ngIf="dashboardActionPending === 'save'" aria-hidden="true"></span>
                {{ dashboardActionPending === 'save' ? (activeSavedDashboardId ? 'Updating…' : 'Saving…') : (activeSavedDashboardId ? 'Update Dashboard' : 'Save Dashboard') }}
              </button>
              <button
                type="button"
                class="primary-action arrange-publish-action"
                *ngIf="dashboardStatus !== 'published'"
                [disabled]="!canSaveDashboard || !!dashboardActionPending"
                (click)="publishDashboard()"
              >
                <span class="btn-spinner" *ngIf="dashboardActionPending === 'publish'" aria-hidden="true"></span>
                {{ dashboardActionPending === 'publish' ? 'Publishing…' : 'Publish' }}
              </button>
              <span class="published-badge" *ngIf="dashboardStatus === 'published'">
                ✓ Published
              </span>
            </div>
          </div>
        </div>
      </mat-step>
    }

    </mat-stepper>

    <!-- <div class="dashboard-config-panel">
      <h3>Generated dashboard config</h3>
      <pre>{{ generatedDashboardConfig || 'Add charts to generate dashboard config.' }}</pre>
    </div> -->
  </section>



